import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Typography, Input, Button, Space, Tooltip, notification, Row, Col, Divider, Skeleton
} from 'antd';
import {
  EnvironmentOutlined, PhoneOutlined, MailOutlined,
  FacebookFilled, InstagramFilled, YoutubeFilled, TwitterOutlined,
  HeartFilled, StarFilled, SafetyCertificateOutlined, CustomerServiceOutlined
} from '@ant-design/icons';
import { useDynamicContent, getContentByKey } from '../../hooks/useDynamicContent';
import api from '../../services/api';

const Footer = () => {
  const year = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const { content, loading } = useDynamicContent('footer');
  const [brandLogo, setBrandLogo] = useState('/images/logo.svg');
  const [platformName, setPlatformName] = useState('Spoon & Spice');

  useEffect(() => {
    const loadBranding = async () => {
      try {
        const res = await api.getPublicSettings();
        const settings = res.data || {};

        if (settings.settings_logo) {
          let logoUrl = settings.settings_logo;
          if (typeof logoUrl === 'string' && logoUrl.startsWith('{')) {
            try {
              const parsed = JSON.parse(logoUrl);
              logoUrl = parsed.value || parsed.url || logoUrl;
            } catch {}
          }
          if (logoUrl && (logoUrl.startsWith('/') || logoUrl.startsWith('http') || logoUrl.startsWith('data:'))) {
            setBrandLogo(logoUrl);
          }
        }

        if (settings.settings_platform_name) {
          let name = settings.settings_platform_name;
          if (typeof name === 'string' && name.startsWith('{')) {
            try {
              const parsed = JSON.parse(name);
              name = parsed.value || name;
            } catch {}
          }
          setPlatformName(name);
        }
      } catch (e) {
        // non-fatal
      }
    };
    loadBranding();
  }, []);

  const handleSubscribe = async () => {
    if (!email || !email.includes('@')) {
      notification.error({
        message: 'Invalid Email',
        description: 'Please enter a valid email address.'
      });
      return;
    }
    
    setSubscribing(true);
    // Simulate API call
    setTimeout(() => {
      notification.success({
        message: 'Subscribed Successfully!',
        description: 'Thank you for subscribing to our newsletter.'
      });
      setEmail('');
      setSubscribing(false);
    }, 1000);
  };

  const socialLinks = [
    { Icon: FacebookFilled, name: 'Facebook', url: getContentByKey(content, 'footer_facebook_url') || 'https://facebook.com/spoonandspice' },
    { Icon: InstagramFilled, name: 'Instagram', url: getContentByKey(content, 'footer_instagram_url') || 'https://instagram.com/spoonandspice' },
    { Icon: YoutubeFilled, name: 'YouTube', url: getContentByKey(content, 'footer_youtube_url') || 'https://youtube.com/@spoonandspice' },
    { Icon: TwitterOutlined, name: 'Twitter', url: getContentByKey(content, 'footer_twitter_url') || 'https://twitter.com/spoonandspice' }
  ];

  const parseLinks = (raw, fallback = []) => {
    try {
      if (!raw) return fallback;
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (Array.isArray(parsed)) {
        return parsed
          .map((it) => ({ name: String(it.name || it.label || ''), url: String(it.url || it.href || '') }))
          .filter((it) => it.name && it.url);
      }
      return fallback;
    } catch (_) {
      return fallback;
    }
  };

  const companyTitle = getContentByKey(content, 'footer_company_title') || 'Company';
  const resourcesTitle = getContentByKey(content, 'footer_resources_title') || 'Resources';
  const contactTitle = getContentByKey(content, 'footer_contact_title') || 'Contact Us';
  const followTitle = getContentByKey(content, 'footer_follow_title') || 'Follow Us';

  const companyLinks = parseLinks(getContentByKey(content, 'footer_company_links'), [
    { name: 'About Us', url: '/about' },
    { name: 'Careers', url: '/careers' },
    { name: 'Contact', url: '/contact' },
    { name: 'Vendors', url: '/vendors' },
  ]);

  const resourceLinks = parseLinks(getContentByKey(content, 'footer_resource_links'), [
    { name: 'Help Center', url: '/help-center' },
    { name: 'Privacy Policy', url: '/privacy-policy' },
    { name: 'Terms of Service', url: '/terms-of-service' },
    { name: 'FAQ', url: '/faq' },
  ]);

  return (
    <footer className="relative bg-gradient-to-br from-slate-900 via-slate-950 to-black text-slate-300 overflow-hidden">
      {/* Background decorative elements - Modern subtle design */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-10 left-10 w-40 h-40 bg-gray-600 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-48 h-48 bg-gray-700 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/3 w-32 h-32 bg-gray-500 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="relative z-10 px-6 py-16">
        <div className="max-w-7xl mx-auto">
          {/* Top Section with Brand and Newsletter */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-r from-[#C97D60] to-[#B8735A] rounded-full flex items-center justify-center">
                <img 
                  src={brandLogo}
                  alt={`${platformName} Logo`}
                  className="w-8 h-8 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const fallback = e.target.nextSibling;
                    if (fallback) fallback.style.display = 'inline';
                  }}
                />
              </div>
              <Typography.Title level={2} className="!text-white !mb-0">
                {loading ? <Skeleton.Input active size="small" style={{ width: 200 }} /> : (getContentByKey(content, 'footer_company_name') || platformName)}
              </Typography.Title>
            </div>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-8">
              {loading ? <Skeleton active paragraph={{ rows: 2 }} /> : getContentByKey(content, 'footer_about_text') || 'India\'s premier catering platform, bringing exceptional culinary experiences to your special moments'}
            </p>
            
            {/* Newsletter Signup - Modern Vibrant Design */}
            <div className="max-w-md mx-auto">
              <div className="flex flex-col sm:flex-row gap-3 p-2 bg-white/10 backdrop-blur-md rounded-xl border border-gray-600/30 shadow-lg hover:bg-white/15 hover:border-gray-500/50 transition-all duration-300">
                <Input
                  placeholder="Enter your email for exclusive offers"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 bg-white/5 border-white/20 text-white placeholder-slate-400 hover:bg-white/10 focus:bg-white/10 focus:border-indigo-500 transition-all rounded-lg"
                  size="large"
                  onPressEnter={handleSubscribe}
                  style={{ borderRadius: '8px' }}
                />
                <Button
                  type="primary"
                  size="large"
                  loading={subscribing}
                  onClick={handleSubscribe}
                  className="btn-vibrant border-none rounded-lg px-6 font-semibold shadow-md hover:shadow-lg transition-all duration-300"
                  style={{ background: '#C97D60', height: '48px' }}
                >
                  Subscribe
                </Button>
              </div>
            </div>
          </div>

          <Divider className="border-slate-700 my-12" />

          {/* Main Footer Content - Modern widget layout */}
          <Row gutter={[32, 32]}>
            {/* Widget 1: Contact */}
            <Col xs={24} sm={12} lg={6}>
              <div className="space-y-6">
                <h4 className="text-white font-bold text-lg mb-4 flex items-center gap-2" data-emoji-context="contact">
                  <CustomerServiceOutlined className="text-[#E8A88A]" />
                  {contactTitle}
                </h4>
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-3 rounded-2xl bg-white/5 hover:bg-white/10 transition-colors">
                    <EnvironmentOutlined className="text-[#E8A88A] mt-1" />
                    <div>
                      <div className="font-medium text-white">Headquarters</div>
                      <div className="text-sm text-slate-400">
                        {loading ? <Skeleton.Input active size="small" style={{ width: 200 }} /> : getContentByKey(content, 'footer_address') || 'Bengaluru, Karnataka, India'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-2xl bg-white/5 hover:bg-white/10 transition-colors">
                    <PhoneOutlined className="text-green-500 mt-1" />
                    <div>
                      <div className="font-medium text-white">Phone</div>
                      <div className="text-sm text-slate-400">
                        {loading ? <Skeleton.Input active size="small" style={{ width: 150 }} /> : getContentByKey(content, 'footer_phone') || '+91 7483423925'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-2xl bg-white/5 hover:bg-white/10 transition-colors">
                    <MailOutlined className="text-blue-500 mt-1" />
                    <div>
                      <div className="font-medium text-white">Email</div>
                      <div className="text-sm text-slate-400">
                        {loading ? <Skeleton.Input active size="small" style={{ width: 200 }} /> : getContentByKey(content, 'footer_email') || 'connect.spoonandspice@gmail.com'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
            {/* Widget 2: Company */}
            <Col xs={24} sm={12} lg={6}>
              <h4 className="text-white font-bold text-lg mb-6" data-emoji-context="contact">{companyTitle}</h4>
              <ul className="space-y-3">
                {companyLinks.map((link, idx) => (
                  <li key={idx}>
                    <Link 
                      to={link.url} 
                      className="text-slate-400 hover:text-white transition-colors block p-2 rounded-lg hover:bg-white/5"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </Col>

            {/* Widget 3: Resources */}
            <Col xs={24} sm={12} lg={6}>
              <h4 className="text-white font-bold text-lg mb-6">{resourcesTitle}</h4>
              <ul className="space-y-3">
                {resourceLinks.map((link, idx) => (
                  <li key={idx}>
                    <Link 
                      to={link.url} 
                      className="text-slate-400 hover:text-white transition-colors block p-2 rounded-lg hover:bg-white/5"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </Col>

            {/* Widget 4: Follow Us */}
            <Col xs={24} sm={12} lg={6}>
              <div className="space-y-6" data-emoji-context="contact">
                <h4 className="text-white font-bold text-lg mb-4">{followTitle}</h4>
                <div className="flex gap-3 flex-wrap">
                  {socialLinks.map((social, idx) => (
                    <Tooltip key={idx} title={social.name}>
                      <a
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="h-14 w-14 flex items-center justify-center rounded-xl bg-white/10 hover:bg-[#C97D60]/20 border border-white/20 hover:border-[#C97D60]/50 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
                      >
                        <social.Icon className="text-slate-300 hover:text-[#E8A88A] text-xl transition-colors duration-300" />
                      </a>
                    </Tooltip>
                  ))}
                </div>
              </div>
            </Col>
          </Row>

          <Divider className="border-slate-700 my-12" />

          {/* Bottom Section */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <span>
                {loading ? <Skeleton.Input active size="small" style={{ width: 200 }} /> : getContentByKey(content, 'footer_copyright') || `© ${year} Spoon & Spice`}
              </span>
              <span>•</span>
              <span>Made with</span>
              <HeartFilled className="text-red-500 text-xs" />
              <span>in India</span>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="hover:text-white transition-colors">
                Terms of Service
              </Link>
              <Link to="/about" className="hover:text-white transition-colors">
                About Us
              </Link>
              <Link to="/contact" className="hover:text-white transition-colors">
                Contact
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
